import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
} from 'react';
import { toast, Toaster } from 'sonner';

const DEFAULT_TOAST_DURATION = 3000;

export type NotificationParams = {
  message: string;
  description?: string;
  duration?: number;
};

export type ToasterContextType = {
  notify: (params: NotificationParams) => void;
  notifyError: (params: NotificationParams) => void;
  notifyInfo: (params: NotificationParams) => void;
  notifySuccess: (params: NotificationParams) => void;
  notifyWarning: (params: NotificationParams) => void;
};

export const ToasterContext = createContext<ToasterContextType>({
  notify: () => void 0,
  notifyError: () => void 0,
  notifyInfo: () => void 0,
  notifySuccess: () => void 0,
  notifyWarning: () => void 0,
});

export function ToasterProvider({ children }: PropsWithChildren) {
  const notify = useCallback(
    ({ message, description, duration }: NotificationParams) => {
      toast(message, {
        description,
        duration: duration ?? DEFAULT_TOAST_DURATION,
      });
    },
    [],
  );

  const notifyError = useCallback((params: NotificationParams) => {
    toast.error(params.message, {
      description: params.description,
      duration: params.duration ?? DEFAULT_TOAST_DURATION,
    });
  }, []);

  const notifyInfo = useCallback((params: NotificationParams) => {
    toast.info(params.message, {
      description: params.description,
      duration: params.duration ?? DEFAULT_TOAST_DURATION,
    });
  }, []);

  const notifySuccess = useCallback((params: NotificationParams) => {
    toast.success(params.message, {
      description: params.description,
      duration: params.duration ?? DEFAULT_TOAST_DURATION,
    });
  }, []);

  const notifyWarning = useCallback((params: NotificationParams) => {
    toast.warning(params.message, {
      description: params.description,
      duration: params.duration ?? DEFAULT_TOAST_DURATION,
    });
  }, []);

  return (
    <ToasterContext.Provider
      value={{ notify, notifyError, notifyInfo, notifySuccess, notifyWarning }}
    >
      <Toaster
        position="top-center"
        richColors
        closeButton
        toastOptions={{
          style: {
            maxWidth: '640px',
            minWidth: 'max-content',
            width: 'max-content',
            padding: '16px',
            wordBreak: 'break-word',
          },
        }}
      />
      {children}
    </ToasterContext.Provider>
  );
}

export function useToaster() {
  const context = useContext(ToasterContext);

  if (!context) {
    throw new Error('useToaster must be used within a ToasterProvider');
  }

  return context;
}
